import { FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PATH from '../../navigation/path'
import styles from './ClaimCardMainInformation.module.css'
import iconEdit from '../../assets/icons/iconEdit.svg'
import { ClaimResponseType, PublicClientDataType } from '../../redux/actions/types'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import CustomButton from '../UI/CustomButton/CustomButton'
import { getCurrentClaim } from '../../redux/actions/claimsAction'
import { getCurrentClient } from '../../redux/actions/clientsAction'
import { ItemInfo } from '../ItemInfo/ItemInfo'
import CustomModal from '../UI/CustomModal/CustomModal'
import OrderInfoModal from '../OrderInfoModal/OrderInfoModal'
import { getCurrentOrder } from '../../redux/actions/ordersAction'

export type ClaimStatus =
  | 'Завершена'
  | 'В работе'
  | 'Отменена'
  | 'Потверждён'
  | 'Предварительная бронь'
  | 'Отклонена'
  | 'Не подтверждена'
export type ClaimCardMainInformationPropsType = {
  claimData: ClaimResponseType | ClaimResponseType[]
  openModal: () => void
  displayButton?: boolean
}

const ClaimCardMainInformation: FC<ClaimCardMainInformationPropsType> = ({
  claimData,
  openModal,
  displayButton,
}) => {
  const { claimId } = useParams<{ claimId: string }>()
  const navigate = useNavigate()
  const [orderModal, setOrderModal] = useState(false)
  const { userRole } = useAppSelector((state) => state.auth)

  const { currentClaim } = useAppSelector((state) => state.claims)

  const dispatch = useAppDispatch()
  const { token } = useAppSelector((state) => state.auth)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const claimId = searchParams.get('id')

    const claimManager = currentClaim?.manager?.fullName
    if (claimId) {
      dispatch(getCurrentClaim({ claimId, token }))
    }
  }, [location, dispatch, token])

  useEffect(() => {
    if (currentClaim?.client?.id && token) {
      dispatch(getCurrentOrder({ token, currentPlace: Number(claimId) }))
      dispatch(getCurrentClient({ id: currentClaim.client.id, token }))
    }
  }, [currentClaim?.client?.id, dispatch, token])

  const onRedirectToEditCardPage = (id?: number) => {
    navigate(`${PATH.editClaimCardPage}${id ? id : claimId}`)
  }

  const handleCreateOrderRedirect = () => {
    navigate(`/collectOrders-page?id=${claimId}`)
  }

  const renderPublicClientData = (publicClientData: PublicClientDataType) => {
    return (
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        <div className={styles.warningBlock}>
          <span className={styles.warningText}>
            Данные клиента из внешнего источника. Необходимо создать клиента вручную в режиме
            редактирования.
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <ItemInfo title='Тип клиента' value={publicClientData.bookingType} />
          {publicClientData.fullName && <ItemInfo title='ФИО' value={publicClientData.fullName} />}
          <ItemInfo title='Телефон' value={publicClientData.phone} />
          <ItemInfo title='Email' value={publicClientData.email} />
          {publicClientData.organizationName && (
            <ItemInfo title='Название организации' value={publicClientData.organizationName} />
          )}
          {publicClientData.inn && <ItemInfo title='ИНН' value={publicClientData.inn} />}
          {publicClientData.contactPerson && (
            <ItemInfo title='ФИО контактного лица' value={publicClientData.contactPerson} />
          )}
          {publicClientData.kpp && <ItemInfo title='КПП' value={publicClientData.kpp} />}
          {publicClientData.ogrn && <ItemInfo title='ОГРН' value={publicClientData.ogrn} />}
          {publicClientData.legalAddress && (
            <ItemInfo title='Юридический адрес' value={publicClientData.legalAddress} />
          )}
          {publicClientData.mailingAddress && (
            <ItemInfo title='Почтовый адрес' value={publicClientData.mailingAddress} />
          )}
          {publicClientData.bankName && <ItemInfo title='Банк' value={publicClientData.bankName} />}
          {publicClientData.signerPosition && (
            <ItemInfo title='Должность подписанта' value={publicClientData.signerPosition} />
          )}
          {publicClientData.contactPhone && (
            <ItemInfo title='Контактный телефон' value={publicClientData.contactPhone} />
          )}
        </div>
      </div>
    )
  }

  const renderClaimInfo = (data: ClaimResponseType) => {
    const startDate = data.startDate.slice(0, 10).split('-').reverse().join('.')
    const startTime = data.startDate.slice(11, 16).split('-').reverse().join('.')
    const endDate = data.endDate.slice(0, 10).split('-').reverse().join('.')
    const endTime = data.endDate.slice(11, 16).split('-').reverse().join('.')
    const price = data?.order?.totalPrice || 0

    return (
      <div className={styles.mainInformationBlock} key={data.id}>
        <CustomModal visible={orderModal} setVisible={setOrderModal}>
          <OrderInfoModal claimInfo={data} onClose={() => setOrderModal(false)} />
        </CustomModal>
        <div className={styles.header}>
          <h2 className={styles.title}>Основная информация</h2>
          {(userRole === 'superAdmin' ||
            userRole === 'admin' ||
            userRole === 'employeePlace' ||
            userRole === 'directorPlace' ||
            userRole === 'directorAgent') && (
            <CustomButton
              iconSrc={iconEdit}
              className={styles.btn}
              title={''}
              onClick={() => onRedirectToEditCardPage(data.id)}
            />
          )}
        </div>

        <div className={styles.mainInformationContainer}>
          {/* Информация о месте */}
          <div className={styles.sectionBlock}>
            <div className={styles.sectionTitle}>Информация о месте</div>
            <div className={styles.sectionContent}>
              <ItemInfo width='30px' height='auto' title='Зал' value={data.place.name} />
              <ItemInfo width='40px' height='auto' title='Город' value={data.place.city} />
              <ItemInfo
                width='120px'
                height='auto'
                title='Менеджер отеля'
                value={data.user.fullName}
              />
              <ItemInfo
                width='210px'
                height='auto'
                title='Стоимость аренды площадки'
                value={String(data.place.establishment?.rentCost) + ' руб.' || '0' + ' руб.'}
              />
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <div className={styles.sectionTitle}>Информация о клиенте</div>
            <div className={styles.sectionContent}>
              {data?.client && (
                <ItemInfo
                  width='50px'
                  height='auto'
                  key={data?.client?.id}
                  title='Клиент'
                  value={String(data?.client?.fullName || data.client.organizationName)}
                />
              )}
              {!data?.client &&
                data?.publicClientData &&
                data?.status === 'unconfirmed' &&
                renderPublicClientData(data.publicClientData)}
              {data?.manager?.fullName && (
                <ItemInfo title='Менеджер' value={data?.manager?.fullName} />
              )}
            </div>
          </div>

          {/* Дата и время */}
          <div className={styles.sectionBlock}>
            <div className={styles.sectionTitle}>Дата и время</div>
            <div className={styles.sectionContent}>
              <ItemInfo
                width='150px'
                height='auto'
                title='Дата и время начала'
                value={`${startDate}, ${startTime}`}
              />
              <ItemInfo
                height='auto'
                title='Дата и время окончания'
                value={`${endDate}, ${endTime}`}
              />
              <ItemInfo
                width='130px'
                height='auto'
                title='Количество гостей'
                value={`${data.peopleCount} человек(a)`}
              />{' '}
              {data.arrangement && (
                <ItemInfo
                  width='90px'
                  height='auto'
                  title='Расстановка'
                  value={data.arrangement ?? ''}
                />
              )}
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <div className={styles.sectionTitle}>Статус и оплата</div>
            <div className={styles.sectionContent}>
              {data?.status == 'rejected' && (
                <ItemInfo
                  width='50px'
                  height='auto'
                  title='Причина отмены'
                  value={data?.rejectionCause ?? ''}
                />
              )}
              {data?.paymentType && (
                <ItemInfo
                  width='110px'
                  height='auto'
                  title='Вид оплаты'
                  value={data?.paymentType ?? ''}
                />
              )}
              <ItemInfo
                width='110px'
                height='auto'
                title='Статус оплаты'
                value={data?.paymentStatus ?? ''}
              />
              {data?.paymentStatus == 'Частично оплачено' && (
                <ItemInfo
                  width='135px'
                  height='auto'
                  title='Сумма предоплаты'
                  value={String(data?.prepaymentAmount) + ' руб.'}
                />
              )}
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <div className={styles.sectionTitle}>Финансовая информация</div>
            <div className={styles.sectionContent}>
              {currentClaim.order != null && price > 0 && (
                <ItemInfo
                  width='100px'
                  height='auto'
                  title='Сумма заказа'
                  value={String(price) + ' руб.'}
                />
              )}
              <ItemInfo
                width='50px'
                height='auto'
                title='Скидка'
                value={`${
                  data.discountAmount != 0
                    ? data.discountAmount + ' руб.'
                    : data.discountPercent + ' %'
                } `}
              />
              <ItemInfo
                width='120px'
                height='auto'
                title='Сервисный сбор'
                value={`${
                  data.serviceFeeAmount != 0
                    ? data.serviceFeeAmount + ' руб.'
                    : data.serviceFeePercent + ' %'
                } `}
              />
              <ItemInfo
                width='120px'
                height='auto'
                title='Пробковый сбор'
                value={`${
                  data.corkFeeAmount != 0
                    ? data.corkFeeAmount + ' руб.'
                    : data.corkFeePercent + ' %'
                } `}
              />
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <div className={styles.sectionTitle}>Дополнительная информация</div>
            <div className={styles.sectionContent}>
              <ItemInfo width='70px' height='auto' title='Сегмент:' value={data.segment ?? ''} />
              {data.order && Array.isArray(data.order)
                ? data.order.map((order, index) => (
                    <div onClick={() => open && open()} key={index}>
                      <ItemInfo
                        width='100px'
                        height='auto'
                        key={order.id || index}
                        title='Номер заказа:'
                        value={String(order.id)}
                      />
                    </div>
                  ))
                : data.order && (
                    <div onClick={() => setOrderModal(true)}>
                      <ItemInfo
                        width='100px'
                        height='auto'
                        order={true}
                        title='Номер заказа:'
                        value={String(data.order.id)}
                        // open={openModal}
                      />
                    </div>
                  )}
            </div>
          </div>
          {/* Службы отеля */}
          {Array.isArray((data as any)?.services) && (data as any).services.length > 0 && (
            <div className={styles.sectionBlock}>
              <div className={styles.sectionTitle}>Службы отеля</div>
              <div className={styles.sectionContent}>
                {((data as any).services as Array<{ name?: string; comment?: string }>).map(
                  (service, index) => (
                    <div
                      key={`service-${index}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 15,
                        paddingBottom: 8,
                        borderBottom:
                          index < ((data as any).services as Array<any>).length - 1
                            ? '1px solid rgba(20, 25, 26, 0.08)'
                            : 'none',
                      }}
                    >
                      {service?.name && (
                        <ItemInfo title={`Служба ${index + 1}`} value={service.name} />
                      )}
                      {service?.comment && (
                        <ItemInfo height='auto' title='Комментарий' value={service.comment} />
                      )}
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Мероприятия */}
          {Array.isArray((data as any)?.events) && (data as any).events.length > 0 && (
            <div className={styles.sectionBlock}>
              <div className={styles.sectionTitle}>Мероприятия</div>
              <div className={styles.sectionContent}>
                {(
                  (data as any).events as Array<{ name?: string; comment?: string; time?: string }>
                ).map((event, index) => (
                  <div
                    key={`event-${index}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 15,
                      paddingBottom: 8,
                      borderBottom:
                        index < ((data as any).events as Array<any>).length - 1
                          ? '1px solid rgba(20, 25, 26, 0.08)'
                          : 'none',
                    }}
                  >
                    {event?.name && (
                      <ItemInfo title={`Мероприятие ${index + 1}`} value={event.name} />
                    )}
                    {event?.comment && (
                      <ItemInfo height='auto' title='Комментарий' value={event.comment} />
                    )}
                    {event?.time && <ItemInfo height='auto' title='Время' value={event.time} />}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={styles.orderButtons}>
            <CustomButton
              className={styles.btn}
              style={displayButton == false ? { display: 'none' } : {}}
              onClick={handleCreateOrderRedirect}
              title={currentClaim?.order ? 'Изменить заказ' : 'Создать заказ'}
            />
          </div>
        </div>
      </div>
    )
  }

  if (Array.isArray(claimData)) {
    return <>{claimData.map((claim) => renderClaimInfo(claim))}</>
  }

  return renderClaimInfo(claimData)
}

export default ClaimCardMainInformation
